import { CheckIcon } from 'lucide-react';
import { useState } from 'react';

import type { IntranetFeedItem } from '@gb-ticket/shared-types';

import { IntranetContentMenu } from '@/products/intranet/components/intranet-content-menu';
import { ChannelIcon } from '@/products/intranet/components/intranet-channels';
import { colleagueVisibleName } from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import {
  editorialCoverUrl,
  editorialPlacementIso,
  editorialTimeLabel,
  showsEditorialHighlight,
} from '@/products/intranet/features/intranet/editorial-calendar';
import { useVoice } from '@/platform/voice/use-voice';
import { cn } from '@/lib/utils';

export function EditorialCalendarCard({
  item,
  channelName,
  channelIcon,
}: {
  item: IntranetFeedItem;
  channelName: string;
  channelIcon: string | null;
}) {
  const voice = useVoice();
  const instant = editorialPlacementIso(item);
  const time = instant ? editorialTimeLabel(instant) : '';

  return (
    <article className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex items-start gap-2 px-2 pt-2">
        <p className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium">
          <ChannelIcon name={channelIcon} />
          <span className="truncate">{channelName}</span>
        </p>
        <IntranetContentMenu item={item} variant="calendar" />
      </div>
      <p className="px-2 pb-2">
        <EditorialStatus isDraft={item.isDraft} />
      </p>
      <div className="space-y-2 px-2 py-2">
        <EditorialThumb item={item} />
        <div className="min-w-0 space-y-1">
          {time ? <p className="text-xs text-muted-foreground tabular-nums">{time}</p> : null}
          <h3 className="line-clamp-2 text-sm font-medium">{item.title}</h3>
          <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <ColleaguePortrait
              displayName={item.createdBy.displayName}
              profilePhotoUrl={item.createdBy.profilePhotoUrl ?? null}
              className="size-5 text-[0.6rem]"
            />
            <span className="truncate">
              {colleagueVisibleName(item.createdBy.displayName, voice)}
            </span>
          </p>
        </div>
      </div>
      {showsEditorialHighlight(item) ? (
        <p className="bg-[#6d28d9] px-2 py-1 text-center text-xs font-medium text-white">
          Highlight
        </p>
      ) : null}
    </article>
  );
}

function EditorialThumb({
  item,
  layout = 'fill',
}: {
  item: IntranetFeedItem;
  layout?: 'fill' | 'compact';
}) {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : editorialCoverUrl(item);
  if (!src) {
    return null;
  }
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md bg-muted',
        layout === 'fill' ? 'aspect-[16/10] w-full' : 'size-10 shrink-0',
      )}
    >
      <img
        src={src}
        alt=""
        aria-hidden
        className="absolute inset-0 size-full scale-110 object-cover blur-md"
      />
      <img
        src={src}
        alt={item.coverAlt ?? ''}
        className="relative size-full object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function EditorialStatus({ isDraft }: { isDraft: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        isDraft
          ? 'border-status-warning-border bg-status-warning text-status-warning-foreground'
          : 'border-status-success-border bg-status-success text-status-success-foreground',
      )}
    >
      {isDraft ? null : <CheckIcon aria-hidden className="size-3" />}
      {isDraft ? 'Geplant' : 'Veröffentlicht'}
    </span>
  );
}
